<template>
  <Card>
    <template #title>
      <div class="flex justify-content-between align-items-center">
        <div class="text-lg font-semibold text-700">{{ title }}</div>
        <div v-if="summaryLoading" class="flex align-items-center gap-2">
          <i class="pi pi-spin pi-spinner text-sm"></i>
          <span class="text-xs text-500">Loading...</span>
        </div>
      </div>
    </template>
    <template #content>
      <div v-if="summaryDisplayCards.length" class="md-summary-wrap">
        <div class="grid md-summary-grid">
          <div
            v-for="card in summaryDisplayCards"
            :key="`${summaryIdPrefix}-${card.title}`"
            class="col-12 sm:col-6 xl:col-3"
          >
            <StatCard
              :title="card.title"
              :content-str="card.value"
              :card-type="card.type"
              :sub-content-str="card.delta"
              :loader="card.loader"
              :is-plus="card.isPositive"
            />
          </div>
        </div>
      </div>
      <!-- <div v-else-if="!summaryLoading" class="md-empty">
        {{ summaryEmptyMessage }}
      </div> -->

      <MetricCardSection
        v-for="section in metricSections"
        :key="section.id"
        :id-prefix="section.id"
        :title="section.title"
        :cards="section.cards"
        :loading="section.loading"
        :loading-message="section.loadingMessage"
        :empty-message="section.emptyMessage"
        :placeholder-labels="section.placeholderLabels || []"
      />

      <RunRateCard
        v-if="runRate"
        :rows="runRate.rows"
        :loading="runRate.loading"
        :editable="runRate.editable"
        @edit="runRate.onEdit && runRate.onEdit($event)"
      />

      <LeaderboardTable
        v-for="table in tables"
        :key="table.id"
        :title="table.title"
        :subtitle="table.subtitle"
        :loading="table.loading"
        :error="table.error"
        :loading-message="table.loadingMessage"
        :empty-message="table.emptyMessage"
        :columns="table.columns"
        :rows="table.rows"
        :period-options="table.periodOptions || []"
        :selected-period="table.selectedPeriod"
        :page-size="table.pageSize"
        :filterable="table.filterable"
        :filter-placeholder="table.filterPlaceholder"
        @update:period="table.onPeriodChange && table.onPeriodChange($event)"
      />
    </template>
  </Card>
</template>

<script setup>
import { defineProps, computed } from "vue";
import Card from "primevue/card";
import StatCard from "@/components/AdminNewDashboard/AdminNewDashboardComponents/StatCard.vue";
import LeaderboardTable from "./LeaderboardTable.vue";
import MetricCardSection from "./MetricCardSection.vue";
import RunRateCard from "./RunRateCard.vue";

const PLACEHOLDER_COUNT = 4;

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  summaryCards: {
    type: Array,
    default: () => [],
  },
  summaryLoading: {
    type: Boolean,
    default: false,
  },
  summaryLoadingMessage: {
    type: String,
    default: "Loading data...",
  },
  summaryEmptyMessage: {
    type: String,
    default: "No data available for this range.",
  },
  summaryIdPrefix: {
    type: String,
    default: "summary",
  },
  summaryPlaceholderLabels: {
    type: Array,
    default: () => [],
  },
  metricSections: {
    type: Array,
    default: () => [],
  },
  runRate: {
    type: Object,
    default: null,
  },
  tables: {
    type: Array,
    default: () => [],
  },
});

const summaryDisplayCards = computed(() => {
  if (props.summaryCards.length) {
    return props.summaryCards.map((card, index) => ({
      title: card.title ?? `summary-${index}`,
      value: props.summaryLoading ? "" : card.value ?? "",
      type: card.type ?? "normal",
      delta: props.summaryLoading ? "" : card.delta ?? "",
      isPositive: card.isPositive ?? true,
      loader: props.summaryLoading ? 1 : 0,
    }));
  }
  if (props.summaryLoading) {
    const labels =
      props.summaryPlaceholderLabels.length > 0
        ? props.summaryPlaceholderLabels
        : Array.from(
            { length: PLACEHOLDER_COUNT },
            (_, index) => `metric-${index}`
          );
    return labels.map((label, index) => ({
      title: label ?? `metric-${index}`,
      value: "",
      type: "normal",
      delta: "",
      isPositive: true,
      loader: 1,
    }));
  }
  return [];
});
</script>

<style scoped>
.md-summary-wrap {
  background: var(--surface-ground);
  margin: -1.5rem -1.5rem 1.5rem;
  padding: 1.5rem;
  border-radius: 12px;
}

.md-summary-grid > [class*="col-"] {
  padding: 0.5rem;
}

.md-empty {
  border: 1px dashed var(--surface-border);
  border-radius: 12px;
  padding: 1.25rem;
  text-align: center;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  background: var(--surface-ground);
}
</style>
